import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectors } from 'store';

export type BundleBuilderPrices = {
  purchaseAmount?: number;
  installmentAmount?: number;
  commitmentPaymentAmount?: number;
  commitmentPaymentDelayDays?: number;
};

export type BundleBuilderEntry = {
  bundleId: string;
  name: string;
  tier: 'essential' | 'elite' | 'master';
  memberCourseIds: string[];
  memberSlugs: string[];
  primaryCourseId?: string;
  prices: BundleBuilderPrices;
  /** False when the bundle has no sellable price; never offer these. */
  purchasable: boolean;
};

export type BundleBuilderCert = {
  courseId: string;
  name: string;
  slug?: string;
  coverImageUrl?: string;
  prices: BundleBuilderPrices;
  /** False when this cert cannot be bought on its own — no gold price, or no content yet. */
  purchasable: boolean;
  /**
   * False for certifications sold inside packages before their content exists.
   *
   * Still pickable: IPTA sells all six in bundles ahead of the standalone launch. The picker labels
   * them so nobody expects to start studying the moment they buy.
   */
  released: boolean;
};

export type BundleBuilderCatalog = {
  bundles: BundleBuilderEntry[];
  certs: BundleBuilderCert[];
  certSlugs: string[];
};

/**
 * Loads the Bundle Builder catalogue.
 *
 * Uses a raw fetch rather than the generated client because these endpoints are not in the
 * committed OpenAPI spec yet — the same approach useBundleUpsell already takes for
 * /courses/:id/public. Swap to the generated client once the spec is regenerated.
 *
 * Deliberately does NOT read from the shared `courses` Redux slice: that slot is written by both
 * getMyCourses and getCourses and is re-fetched globally on every token change, so loading a
 * catalogue through it would corrupt the Billing page's owned-bundles list.
 */
export const useBundleBuilderCatalog = (enabled: boolean) => {
  const accessToken = useSelector(selectors.auth.selectAccessToken);
  const [catalog, setCatalog] = useState<BundleBuilderCatalog | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAccessToken = useCallback(
    () =>
      accessToken ||
      localStorage.getItem('access_token') ||
      localStorage.getItem('accessToken') ||
      localStorage.getItem('token') ||
      '',
    [accessToken],
  );

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    const baseUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:4000';

    const load = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`${baseUrl}/courses/bundle-builder/catalog`, {
          headers: {
            Authorization: `Bearer ${getAccessToken()}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) throw new Error(`Catalogue request failed (${response.status})`);

        const data: BundleBuilderCatalog = await response.json();
        if (cancelled) return;

        // An unsellable package would render a price-less card and then fail at checkout, so it
        // is filtered here rather than shown. The backend flags them and logs a warning naming
        // the bundle so the missing price is fixable in admin.
        setCatalog({
          ...data,
          bundles: (data.bundles ?? []).filter((b) => b.purchasable),
          certs: data.certs ?? [],
        });
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load packages');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [enabled, getAccessToken]);

  return { catalog, isLoading, error };
};

/** Resolves an exact set of picked certifications to the one bundle that contains precisely them. */
export const resolveBundleForPicks = async (courseIds: string[], token: string): Promise<string | null> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:4000';

  const response = await fetch(`${baseUrl}/courses/bundle-builder/resolve`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ courseIds }),
  });

  // 404 is expected and meaningful: that exact combination is not sold as a package.
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`Could not resolve that combination (${response.status})`);

  const data: { bundleId: string } = await response.json();
  return data.bundleId;
};
